import React from "react";
import { 
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { CollegeFilters } from "@/hooks/useCollegeSearch";
import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";

export interface CollegeSearchFiltersProps {
  filters: CollegeFilters;
  filterOptions: {
    states: string[];
    divisions: string[];
    conferences: string[];
    religiousAffiliations: string[];
    sports?: { label: string; columnMen: string; columnWomen: string }[];
    tuitionRange?: [number, number];
    actRange?: [number, number]; 
    satRange?: [number, number];
    gpaRange?: [number, number];
  };
  setFilters: React.Dispatch<React.SetStateAction<CollegeFilters>>;
  onApplyFilters: () => void;
  loading: boolean;
}

const CollegeSearchFilters: React.FC<CollegeSearchFiltersProps> = ({ 
  filters, 
  filterOptions,
  setFilters,
  onApplyFilters,
  loading
}) => {
  const handleStateChange = (state: string) => {
    const newStates = filters.states.includes(state)
      ? filters.states.filter(s => s !== state)
      : [...filters.states, state];
    setFilters(prev => ({ ...prev, states: newStates }));
    // Auto-apply filters when state changes
    setTimeout(() => onApplyFilters(), 100);
  };

  const handleDivisionChange = (division: string) => {
    const newDivisions = filters.divisions.includes(division)
      ? filters.divisions.filter(d => d !== division)
      : [...filters.divisions, division];
    setFilters(prev => ({ ...prev, divisions: newDivisions }));
    // Auto-apply filters when division changes
    setTimeout(() => onApplyFilters(), 100);
  };

  const handleConferenceChange = (conference: string) => {
    const newConferences = filters.conferences.includes(conference)
      ? filters.conferences.filter(c => c !== conference)
      : [...filters.conferences, conference];
    setFilters(prev => ({ ...prev, conferences: newConferences }));
    // Auto-apply filters when conference changes
    setTimeout(() => onApplyFilters(), 100);
  };

  const handleReligiousAffiliationChange = (affiliation: string) => {
    const newAffiliations = filters.religiousAffiliations.includes(affiliation)
      ? filters.religiousAffiliations.filter(a => a !== affiliation)
      : [...filters.religiousAffiliations, affiliation];
    setFilters(prev => ({ ...prev, religiousAffiliations: newAffiliations }));
    // Auto-apply filters when religious affiliation changes
    setTimeout(() => onApplyFilters(), 100);
  };

  const resetFilters = () => {
    setFilters({
      states: [],
      divisions: [],
      conferences: [],
      religiousAffiliations: [],
      tuitionMin: null,
      tuitionMax: null,
      satMin: null,
      satMax: null,
      actMin: null,
      actMax: null,
      gpaMin: null,
      gpaMax: null,
      sports: [],
      searchQuery: '',
    });
  };

  const hasActiveFilters = () => {
    return (
      filters.states.length > 0 ||
      filters.divisions.length > 0 ||
      filters.conferences.length > 0 ||
      filters.religiousAffiliations.length > 0 ||
      filters.tuitionMin !== null ||
      filters.tuitionMax !== null ||
      filters.satMin !== null ||
      filters.satMax !== null ||
      filters.actMin !== null ||
      filters.actMax !== null ||
      filters.gpaMin !== null ||
      filters.gpaMax !== null ||
      filters.sports.length > 0
    );
  };

  return (
    <div className="border rounded-lg p-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium">Filters</h3>
        {hasActiveFilters() && (
          <Button variant="ghost" size="sm" onClick={resetFilters}>
            Reset All
          </Button>
        )}
      </div>
      
      {/* Active filters */}
      {hasActiveFilters() && (
        <div className="mb-4">
          <div className="flex flex-wrap gap-2">
            {filters.states.map(state => (
              <Badge key={state} variant="secondary" className="flex items-center gap-1">
                {state}
                <X
                  className="h-3 w-3 ml-1 cursor-pointer"
                  onClick={() => handleStateChange(state)}
                />
              </Badge>
            ))}
            
            {filters.divisions.map(division => (
              <Badge key={division} variant="secondary" className="flex items-center gap-1">
                {division}
                <X
                  className="h-3 w-3 ml-1 cursor-pointer"
                  onClick={() => handleDivisionChange(division)}
                />
              </Badge>
            ))}
            
            {filters.conferences.map(conference => (
              <Badge key={conference} variant="secondary" className="flex items-center gap-1">
                {conference}
                <X
                  className="h-3 w-3 ml-1 cursor-pointer"
                  onClick={() => handleConferenceChange(conference)}
                />
              </Badge>
            ))}
            
            {filters.religiousAffiliations.map(affiliation => (
              <Badge key={affiliation} variant="secondary" className="flex items-center gap-1">
                {affiliation}
                <X
                  className="h-3 w-3 ml-1 cursor-pointer"
                  onClick={() => handleReligiousAffiliationChange(affiliation)}
                />
              </Badge>
            ))}
          </div>
        </div>
      )}

      <Accordion type="multiple" className="w-full">
        <AccordionItem value="location">
          <AccordionTrigger>States</AccordionTrigger>
          <AccordionContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {filterOptions.states.map(state => (
                <div key={state} className="flex items-center space-x-2">
                  <Checkbox
                    id={`state-${state}`}
                    checked={filters.states.includes(state)}
                    onCheckedChange={() => handleStateChange(state)}
                  />
                  <Label
                    htmlFor={`state-${state}`}
                    className="text-sm cursor-pointer"
                  >
                    {state}
                  </Label>
                </div>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="divisions">
          <AccordionTrigger>Divisions</AccordionTrigger>
          <AccordionContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {filterOptions.divisions.map(division => (
                <div key={division} className="flex items-center space-x-2">
                  <Checkbox
                    id={`division-${division}`}
                    checked={filters.divisions.includes(division)}
                    onCheckedChange={() => handleDivisionChange(division)}
                  />
                  <Label
                    htmlFor={`division-${division}`}
                    className="text-sm cursor-pointer"
                  >
                    {division}
                  </Label>
                </div>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="conferences">
          <AccordionTrigger>Conferences</AccordionTrigger>
          <AccordionContent>
            <div className="grid grid-cols-2 gap-2">
              {filterOptions.conferences.slice(0, 20).map(conference => (
                <div key={conference} className="flex items-center space-x-2">
                  <Checkbox
                    id={`conference-${conference}`}
                    checked={filters.conferences.includes(conference)}
                    onCheckedChange={() => handleConferenceChange(conference)}
                  />
                  <Label
                    htmlFor={`conference-${conference}`}
                    className="text-sm cursor-pointer"
                  >
                    {conference}
                  </Label>
                </div>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="religious">
          <AccordionTrigger>Religious Affiliation</AccordionTrigger>
          <AccordionContent>
            <div className="grid grid-cols-2 gap-2">
              {filterOptions.religiousAffiliations.map(affiliation => (
                <div key={affiliation} className="flex items-center space-x-2">
                  <Checkbox
                    id={`religion-${affiliation}`}
                    checked={filters.religiousAffiliations.includes(affiliation)}
                    onCheckedChange={() => handleReligiousAffiliationChange(affiliation)}
                  />
                  <Label
                    htmlFor={`religion-${affiliation}`}
                    className="text-sm cursor-pointer"
                  >
                    {affiliation}
                  </Label>
                </div>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="tuition">
          <AccordionTrigger>Tuition Range</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="tuition-min">Minimum ($)</Label>
                  <Input
                    id="tuition-min"
                    type="number"
                    placeholder="0"
                    value={filters.tuitionMin || ''}
                    onChange={(e) => {
                      const value = e.target.value ? parseInt(e.target.value) : null;
                      setFilters(prev => ({ ...prev, tuitionMin: value }));
                      // Auto-apply filters after a brief delay
                      setTimeout(() => onApplyFilters(), 500);
                    }}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tuition-max">Maximum ($)</Label>
                  <Input
                    id="tuition-max"
                    type="number"
                    placeholder="100000"
                    value={filters.tuitionMax || ''}
                    onChange={(e) => {
                      const value = e.target.value ? parseInt(e.target.value) : null;
                      setFilters(prev => ({ ...prev, tuitionMax: value }));
                      // Auto-apply filters after a brief delay
                      setTimeout(() => onApplyFilters(), 500);
                    }}
                  />
                </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="satact">
          <AccordionTrigger>Test Scores</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-medium mb-2">SAT Score Range</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="sat-min">Minimum</Label>
                    <Input
                      id="sat-min"
                      type="number"
                      placeholder="400"
                      min="400"
                      max="1600"
                      value={filters.satMin || ''}
                      onChange={(e) => {
                        const value = e.target.value ? parseInt(e.target.value) : null;
                        setFilters(prev => ({ ...prev, satMin: value }));
                        setTimeout(() => onApplyFilters(), 500);
                      }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="sat-max">Maximum</Label>
                    <Input
                      id="sat-max"
                      type="number"
                      placeholder="1600"
                      min="400"
                      max="1600"
                      value={filters.satMax || ''}
                      onChange={(e) => {
                        const value = e.target.value ? parseInt(e.target.value) : null;
                        setFilters(prev => ({ ...prev, satMax: value }));
                        setTimeout(() => onApplyFilters(), 500);
                      }}
                    />
                  </div>
                </div>
              </div>
              
              <div>
                <h4 className="text-sm font-medium mb-2">ACT Score Range</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="act-min">Minimum</Label>
                    <Input
                      id="act-min"
                      type="number"
                      placeholder="1"
                      min="1"
                      max="36"
                      value={filters.actMin || ''}
                      onChange={(e) => {
                        const value = e.target.value ? parseInt(e.target.value) : null;
                        setFilters(prev => ({ ...prev, actMin: value }));
                        setTimeout(() => onApplyFilters(), 500);
                      }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="act-max">Maximum</Label>
                    <Input
                      id="act-max"
                      type="number"
                      placeholder="36"
                      min="1"
                      max="36"
                      value={filters.actMax || ''}
                      onChange={(e) => {
                        const value = e.target.value ? parseInt(e.target.value) : null;
                        setFilters(prev => ({ ...prev, actMax: value }));
                        setTimeout(() => onApplyFilters(), 500);
                      }}
                    />
                  </div>
                </div>
              </div>
              
              <div>
                <h4 className="text-sm font-medium mb-2">GPA Range</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="gpa-min">Minimum</Label>
                    <Input
                      id="gpa-min"
                      type="number"
                      placeholder="0.0"
                      min="0"
                      max="4.0"
                      step="0.1"
                      value={filters.gpaMin || ''}
                      onChange={(e) => {
                        const value = e.target.value ? parseFloat(e.target.value) : null;
                        setFilters(prev => ({ ...prev, gpaMin: value }));
                        setTimeout(() => onApplyFilters(), 500);
                      }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="gpa-max">Maximum</Label>
                    <Input
                      id="gpa-max"
                      type="number"
                      placeholder="4.0"
                      min="0"
                      max="4.0"
                      step="0.1"
                      value={filters.gpaMax || ''}
                      onChange={(e) => {
                        const value = e.target.value ? parseFloat(e.target.value) : null;
                        setFilters(prev => ({ ...prev, gpaMax: value }));
                        setTimeout(() => onApplyFilters(), 500);
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};

export default CollegeSearchFilters;
